import {FC, useEffect, useState} from 'react'
import Flatpickr from 'react-flatpickr'
import 'flatpickr/dist/flatpickr.css'
import {ImageInputComponent} from '../_metronic/assets/ts/components'
import {toAbsoluteUrl} from '../_metronic/helpers'
import {toast} from 'react-toastify'
import {useThemeMode} from '../_metronic/partials'
import {fileSrc} from './fileUtils'
import {FieldMetaProps} from 'formik'
import ReactSelect, {GroupBase, OptionsOrGroups} from 'react-select'
import CreatableSelect from 'react-select/creatable'

type Props = {
  value: string | number | Date | readonly (string | number | Date)[] | undefined
  defaultValue: string | undefined
  inputRef: any
}

export const types = {
  allFiles: '*/*',
  images: 'image/*',
  pdf: 'application/pdf',
  csv: 'text/csv',
  doc: 'application/msword',
  docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  xls: 'application/vnd.ms-excel',
  xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  ppt: 'application/vnd.ms-powerpoint',
  pptx: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
} as const

export const docTypes = ['doc', 'docx', 'csv', 'pdf', 'xls', 'xlsx', 'ppt', 'pptx'] as const

export const CustomInput: FC<Props> = ({value, defaultValue, inputRef, ...props}) => {
  return <input {...props} defaultValue={defaultValue} ref={inputRef} />
}

export const DatePicker: FC<any> = ({field, form, ...props}) => {
  return (
    <Flatpickr
      onChange={([date]) => form.setFieldValue(field.name, date)}
      value={field.value}
      {...props}
      render={({defaultValue, value, ...props}, ref) => {
        return <CustomInput defaultValue={defaultValue} inputRef={ref} value={value} {...props} />
      }}
    />
  )
}

export const FileUpload: FC<any> = ({field, form, ...props}) => {
  const [error, setError] = useState<string | undefined>()
  const limit = props.limit || field.fileProps?.limit || 250
  const value = form.getFieldMeta(field.name).value
  const removeFile = () => form.setFieldValue(field.name, '')
  return (
    <>
      <input
        {...props}
        className='form-control form-control-lg form-control-solid'
        type='file'
        onChange={(e) => {
          if (e.target.files?.length) {
            if (limit && e.target.files[0].size > limit * 1024) {
              toast.error(
                `File too large!! max size is ${limit >= 1024 ? limit / 1024 : limit}${
                  limit >= 1024 ? 'mb' : 'kb'
                }`
              )
              setError(
                `File too large!! max size is ${limit >= 1024 ? limit / 1024 : limit}${
                  limit >= 1024 ? 'mb' : 'kb'
                }`
              )
            } else {
              form.setFieldValue(field.name, e.target.files[0])
              setError(undefined)
            }
          }
        }}
        accept={props.accept || field.fileProps?.accept || '.pdf, .doc, .docx, .ppt, .pptx'}
      />
      {error && <div className='text-danger mt-2'>{error}</div>}
      {value && (
        <>
          <a
            target='_blank'
            href={value instanceof File ? value.name : fileSrc(value)}
            rel='noreferrer'
            download
          >
            View/Download <i className='fa-solid fa-up-right-from-square text-primary'></i>
          </a>
          <i className='fa-solid fa-trash cursor-pointer text-danger mx-2' onClick={removeFile}></i>
        </>
      )}
    </>
  )
}

export const ImageUpload: FC<any> = ({field, form, ...props}) => {
  const value = form.getFieldMeta(field.name).value
  const {mode} = useThemeMode()
  const blankImg =
    props.blankImg ||
    toAbsoluteUrl(`/media/svg/files/blank-image${mode === 'dark' ? '-dark' : ''}.svg`)
  // const blankImg = toAbsoluteUrl('/media/svg/avatars/blank.svg')
  const [error, setError] = useState<string | undefined>()
  useEffect(() => {
    ImageInputComponent.createInsance(
      `#${field.name.replaceAll('[', '_').replaceAll(']', '').split('.')[0]}`
    )
  }, [field.name])
  const addImageProcess = (src: string): Promise<{height: number; width: number}> => {
    return new Promise((resolve, reject) => {
      let img = new Image()
      img.onload = () => resolve({height: img.height, width: img.width})
      img.onerror = reject
      img.src = src
    })
  }
  const uploadFile = async (file: File) => {
    let err = ''
    if (file.size > (field.fileProps?.limit || 120) * 1024) {
      err += `File too large!! max size is ${
        field.fileProps?.limit >= 1024
          ? (field.fileProps?.limit || 120) / 1024
          : field.fileProps?.limit || 120
      }${field.fileProps?.limit >= 1024 ? 'mb' : 'kb'} `
    }
    let image = document.getElementById('image-display') as any
    if (image !== null) {
      let img = new Image()
      img.src = URL.createObjectURL(file)
      const dimension = await addImageProcess(img.src)
      if (field.fileProps?.maxWidth && dimension.width > Number(field.fileProps?.maxWidth))
        err += `Maximum width of ${field.fileProps?.maxWidth} `
      if (field.fileProps?.maxHeight && dimension.height > Number(field.fileProps?.maxHeight))
        err += `Maximum height of ${field.fileProps?.maxHeight} `
      if (field.fileProps?.uploadWidth && dimension.width !== Number(field.fileProps?.uploadWidth))
        err += `Width must be ${field.fileProps?.uploadWidth} `
      if (
        field.fileProps?.uploadHeight &&
        dimension.height !== Number(field.fileProps?.uploadHeight)
      )
        err += `Height must be ${field.fileProps?.uploadHeight} `
      URL.revokeObjectURL(img.src)
    }
    if (err) {
      toast.error(err)
      setError(err)
      setTimeout(function () {
        document.getElementById('custom_remove')?.click()
      }, 100)
    } else {
      setError(undefined)
      form.setFieldValue(field.name, file)
    }
  }
  const removeFile = () => form.setFieldValue(field.name, undefined)
  return (
    <div>
      <img id='image-display' style={{display: 'none'}} alt='dimension checker' />
      <div
        id={field.name.replaceAll('[', '_').replaceAll(']', '').split('.')[0]}
        className='image-input image-input-outline'
        data-kt-image-input='true'
        style={{
          backgroundImage: `url('${blankImg}')`,
          backgroundSize: 'contain',
          backgroundPosition: 'center',
        }}
      >
        {/* begin::Preview existing avatar */}
        <div
          className={`image-input-wrapper w-${props.width || '125'}px h-${props.height || '125'}px`}
          style={{
            backgroundImage: `url('${
              value
                ? value instanceof File
                  ? URL.createObjectURL(value)
                  : fileSrc(value)
                : blankImg
            }')`,
            backgroundSize: 'contain',
            backgroundPosition: 'center',
          }}
        ></div>
        {/* end::Preview existing avatar */}

        {/* begin::Label */}
        <label
          className='btn btn-icon btn-circle btn-active-color-primary w-25px h-25px bg-body shadow'
          data-kt-image-input-action='change'
          data-bs-toggle='tooltip'
          title='Change avatar'
        >
          <i className='bi bi-pencil-fill fs-7'></i>

          <input
            type='file'
            onChange={(e) => {
              if (e.target.files) {
                uploadFile(e.target.files[0])
              }
            }}
            name={field.name}
            accept='.png, .jpg, .jpeg'
          />
          <input type='hidden' name='avatar_remove' />
        </label>
        {/* end::Label */}

        {/* begin::Cancel */}
        <span
          className='btn btn-icon btn-circle btn-active-color-primary w-25px h-25px bg-body shadow'
          data-kt-image-input-action='cancel'
          data-bs-toggle='tooltip'
          title='Cancel avatar'
          id='custom_cancel'
        >
          <i className='bi bi-x fs-2'></i>
        </span>
        {/* end::Cancel */}

        {/* begin::Remove */}
        <span
          className='btn btn-icon btn-circle btn-active-color-primary w-25px h-25px bg-body shadow'
          data-kt-image-input-action='remove'
          data-bs-toggle='tooltip'
          title='Remove avatar'
          id='custom_remove'
          onClick={removeFile}
        >
          <i className='bi bi-x fs-2'></i>
        </span>
        {/* end::Remove */}
      </div>
      {error && <div className='text-danger mt-2'>{error}</div>}
    </div>
  )
}

export const Switch: FC<any> = ({field, ...rest}) => {
  return (
    <div className='form-check form-switch form-switch-sm form-check-custom form-check-solid'>
      <input
        name={field.name}
        className='form-check-input'
        type='checkbox'
        defaultChecked={field.value}
        onChange={rest.onChange}
        disabled={rest.disabled}
        checked={rest.value}
      />
    </div>
  )
}

type InputProps = {
  meta: FieldMetaProps<string>
  label: string
  onChange: any
  required?: boolean
  disabled?: boolean
  isMulti?: boolean
  options?: OptionsOrGroups<any, GroupBase<any>>
  name?: string
  isLoading?: boolean
  allowCreate?: boolean
  validator?: any
}
export const UseSelect = ({
  label,
  meta,
  options,
  onChange,
  allowCreate,
  validator,
  ...props
}: InputProps) => {
  return (
    <div>
      <label htmlFor={props.name || label} className='block flex text-sm mb-1 text-[#7C7F80]'>
        {label}
      </label>
      {allowCreate ? (
        <CreatableSelect
          {...props}
          value={
            meta?.value?.length
              ? props.isMulti
                ? meta.value
                : {
                    value: meta.value,
                    label: options?.find((o) => o.value === meta.value)?.label || meta.value,
                  }
              : null
          }
          options={options}
          className='basic-multi-select'
          classNamePrefix='select'
          onChange={(selectedOption: any) => {
            onChange(selectedOption)
          }}
          isValidNewOption={(inputValue: any) => {
            return validator ? validator(inputValue) : true
          }}
        />
      ) : (
        <ReactSelect
          {...props}
          value={
            Array.isArray(meta?.value)
              ? props.isMulti
                ? meta.value.map((val: any) => ({
                    value: val,
                    label: options?.find((o) => o.value === val)?.label || val,
                  }))
                : null
              : meta?.value
              ? {
                  value: meta.value,
                  label: options?.find((o) => o.value === meta.value)?.label || meta.value,
                }
              : null
          }
          options={options}
          className='basic-multi-select'
          classNamePrefix='select'
          onChange={(selectedOption: any) => {
            onChange(selectedOption)
          }}
        />
      )}
      {meta.touched && meta.error && <div className='text-red-500 text-sm'>{meta.error}</div>}
    </div>
  )
}
